import _ from "lodash";
import { getFormattedUserName } from "Utils/GetFormattedUserName";
import { goToRelativeRoute } from "modules/Services";
import {
  SIDEBAR_NODE_ID_TO_ROUTE_PATH_MAP,
  TRANSCRIPT_ROUTES,
  STUDENT_FEED_COLUMN_IDS,
  QUERY_PARAMS_TO_PRESERVE,
  EQUALITY_TYPES,
} from "./constants";
import moment from "moment";
import { scrollbarWidth } from "TranscriptsV3/routes/StudentDetails/routes/GradeLevels/modules/utils/dataGridHelpers";

export const navigateWithPreservedParams = ({
  route,
  paramsToPreserve = QUERY_PARAMS_TO_PRESERVE,
  type = "push",
  replacePath = null,
  state = {},
  additionalParams = {},
}) => {
  return dispatch => {
    if (!route) {
      return;
    }

    const paramsToAdd = { ...additionalParams };

    // Preserve specified query params from current location
    if (!_.isEmpty(paramsToPreserve)) {
      const currentSearch = window.location.search;
      if (currentSearch) {
        const searchParams = new URLSearchParams(currentSearch);

        _.forEach(paramsToPreserve, param => {
          const value = searchParams.get(param);
          if (!_.isNil(value) && value !== "") {
            paramsToAdd[param] = value;
          }
        });
      }
    }

    // Build route with query params
    let finalRoute = route;
    if (!_.isEmpty(paramsToAdd)) {
      const queryString = new URLSearchParams(paramsToAdd);
      finalRoute = `${route}?${queryString}`;
    }

    // Dispatch navigation
    dispatch(
      goToRelativeRoute({
        route: finalRoute,
        type,
        location: null,
        replacePath,
        state,
      })
    );
  };
};

const ARCHIVAL_STATE_OPTIONS = [
  { value: "ACTIVE", label: "common:active" },
  { value: "ARCHIVED", label: "common:archived" },
];

export const getArchivalStateOptions = t =>
  _.map(ARCHIVAL_STATE_OPTIONS, option => ({
    value: option.value,
    label: t(option.label),
  }));

export const getChecklistDropdownOptions = ({
  valueOptions = [],
  shouldShowIcon = false,
}) =>
  _.map(valueOptions, option => {
    if (_.has(option, "options")) {
      return {
        key: option.schoolId ?? option.key ?? option.label,
        label: option.label,
        isItemGroup: true,
        options: _.map(_.get(option, "options", []), nestedOption => ({
          ..._.omit(nestedOption, ["icon", "subtitle"]),
          key: nestedOption.value,
          subText: nestedOption.subtitle,
        })),
      };
    }

    const icon = _.get(option, "icon");
    return {
      ..._.omit(option, ["icon", "subtitle"]),
      key: option.value,
      subText: option.subtitle,
      ...(shouldShowIcon && icon ? { prefix: icon } : {}),
    };
  });

/**
 * Sort options for student list
 */
export const SORT_OPTIONS = [
  { value: "FULL_NAME", label: "common:name" },
  { value: "CURRICULUM_PROGRAM", label: "common:school" },
];

export const getAppliedStudentFilters = ({
  filters = {},
  schoolOptions = [],
  getGroupedYearGroups = () => [],
  allowedSchoolIds = [],
}) => {
  const filtersCopy = { ...filters };

  // Resolve base school IDs, respecting permission restrictions.
  filtersCopy.curriculumProgramIds = resolveBaseSchoolIds({
    filtersCopy,
    allowedSchoolIds,
  });

  // If the intersection produced no valid schools, bail out early.
  if (
    !_.isEmpty(allowedSchoolIds) &&
    _.isEmpty(filtersCopy.curriculumProgramIds)
  ) {
    filtersCopy.curriculumProgramIds = [];
    return filtersCopy;
  }

  if (
    _.get(filters, "schoolIdsEquality") === EQUALITY_TYPES.IS_NOT &&
    !_.isEmpty(filters.curriculumProgramIds)
  ) {
    filtersCopy.curriculumProgramIds = resolveIsNotSchoolFilter({
      filters,
      filtersCopy,
      allowedSchoolIds,
      schoolOptions,
    });
  }

  if (
    _.get(filters, "yearGroupIdsEquality") === EQUALITY_TYPES.IS_NOT &&
    !_.isEmpty(filters.yearGroupIds)
  ) {
    filtersCopy.yearGroupIds = resolveIsNotYearGroupFilter({
      filters,
      filtersCopy,
      getGroupedYearGroups,
    });
  }

  if (
    _.get(filters, "archivalStateEquality") === EQUALITY_TYPES.IS_NOT &&
    _.get(filters, "archivalState")
  ) {
    filtersCopy.archivalState =
      _.get(filters, "archivalState") === "ACTIVE" ? "ARCHIVED" : "ACTIVE";
  }

  delete filtersCopy.schoolIdsEquality;
  delete filtersCopy.yearGroupIdsEquality;
  delete filtersCopy.archivalStateEquality;

  return filtersCopy;
};

/**
 * Restricts a list of schools to only the one matching the given schoolId.
 * When no schoolId is provided the full list is returned unchanged.
 * Use this wherever a school-portal context should limit the school picker
 * to a single entry (e.g. MapSchoolsModal, Feedlist).
 */
export const filterSchoolsForContext = (schools, schoolId) =>
  schoolId ? _.filter(schools, s => s.id === schoolId) : schools;

// ─── Private helpers for getAppliedStudentFilters ────────────────────────────

const resolveBaseSchoolIds = ({ filtersCopy, allowedSchoolIds }) => {
  const hasRestriction = !_.isEmpty(allowedSchoolIds);
  if (_.isEmpty(filtersCopy.curriculumProgramIds)) {
    return hasRestriction ? allowedSchoolIds : [];
  }
  return hasRestriction
    ? _.intersection(filtersCopy.curriculumProgramIds, allowedSchoolIds)
    : filtersCopy.curriculumProgramIds;
};

const resolveIsNotSchoolFilter = ({
  filters,
  allowedSchoolIds,
  schoolOptions,
}) => {
  const allSchoolIds = !_.isEmpty(allowedSchoolIds)
    ? allowedSchoolIds
    : _.map(schoolOptions, "value");
  return _.difference(allSchoolIds, filters.curriculumProgramIds);
};

const resolveIsNotYearGroupFilter = ({
  filters,
  filtersCopy,
  getGroupedYearGroups,
}) => {
  const relevantSchoolIds = _.isEmpty(filtersCopy.curriculumProgramIds)
    ? []
    : filtersCopy.curriculumProgramIds;
  const allYearGroupOptions = _.isEmpty(relevantSchoolIds)
    ? getGroupedYearGroups([])
    : getGroupedYearGroups(relevantSchoolIds);
  const allYearGroupIds = _.flatMap(allYearGroupOptions, option =>
    _.isArray(_.get(option, "options"))
      ? _.map(_.get(option, "options", []), "value")
      : [_.get(option, "value")]
  );
  return _.difference(allYearGroupIds, filters.yearGroupIds);
};

// ─────────────────────────────────────────────────────────────────────────────

export const getStudentFeedVariables = ({
  organizationId,
  filters,
  sortState,
  after = null,
  academicYearId,
}) => {
  const queryFilters = {};

  const archivalState = _.get(filters, "archivalState") || "ACTIVE";
  if (!_.isEmpty(archivalState)) {
    queryFilters.archivalState = archivalState;
  }

  const searchText = _.get(filters, "searchText");
  if (!_.isEmpty(searchText)) {
    queryFilters.searchText = searchText;
    queryFilters.searchTextFields = ["NAME", "SOURCED_ID"];
  }

  const curriculumProgramIds = _.get(filters, "curriculumProgramIds");
  if (!_.isEmpty(curriculumProgramIds)) {
    queryFilters.curriculumProgramIds = curriculumProgramIds;
  }

  const yearGroupIds = _.get(filters, "yearGroupIds");
  if (!_.isEmpty(yearGroupIds)) {
    queryFilters.yearGroups = yearGroupIds;
  }

  if (academicYearId) {
    queryFilters.academicYearId = academicYearId;
  }

  const variables = {
    first: 20,
    organizationId,
    after,
    filters: queryFilters,
    orderBy: _.get(sortState, "orderBy", "FULL_NAME"),
    orderByDirection: _.get(sortState, "orderByDirection", "ASC"),
  };

  if (academicYearId) {
    variables.studentCurriculumProgramFilter = { academicYearId };
    variables.userYearGroupMapFilters = { academicYearIds: [academicYearId] };
    variables.yearGroupGradeFilters = { academicYearId };
    variables.userArchivedFilters = { academicYearId };
  }

  return variables;
};

/**
 * UI Types for FeedList columns
 */
export const FEED_LIST_UI_TYPES = {
  TEXT_WITH_AVATAR_AND_SUBTEXT: "TEXT_WITH_AVATAR_AND_SUBTEXT",
  TEXT: "TEXT",
  DOWNLOAD_BUTTON: "DOWNLOAD_BUTTON",
  MORE_ITEMS_WITH_TOOLTIP: "MORE_ITEMS_WITH_TOOLTIP",
};

export const getStudentFeedColumns = ({ t, showSchoolColumn = true }) => {
  const columns = [
    {
      id: STUDENT_FEED_COLUMN_IDS.STUDENT_NAME,
      type: "CUSTOM",
      label: t("common:student_name"),
      uiType: FEED_LIST_UI_TYPES.TEXT_WITH_AVATAR_AND_SUBTEXT,
      isSticky: true,
      style: {
        minWidth: "200px",
        maxWidth: "200px",
      },
    },
  ];

  if (showSchoolColumn) {
    columns.push({
      id: STUDENT_FEED_COLUMN_IDS.SCHOOL,
      type: "CUSTOM",
      label: t("common:school"),
      uiType: FEED_LIST_UI_TYPES.TEXT_WITH_AVATAR_AND_SUBTEXT,
      style: {
        minWidth: "200px",
        maxWidth: "200px",
      },
    });
  }

  columns.push(
    {
      id: STUDENT_FEED_COLUMN_IDS.YEAR_GROUP,
      type: "CUSTOM",
      label: t("common:year_group"),
      uiType: FEED_LIST_UI_TYPES.TEXT_WITH_AVATAR_AND_SUBTEXT,
      style: {
        minWidth: "150px",
        maxWidth: "200px",
      },
    },
    {
      id: STUDENT_FEED_COLUMN_IDS.GRADE_LEVELS,
      type: "CUSTOM",
      label: t("transcript:grade_levels_available"),
      uiType: FEED_LIST_UI_TYPES.MORE_ITEMS_WITH_TOOLTIP,
      style: {
        minWidth: "180px",
        maxWidth: "220px",
      },
    },
    {
      id: STUDENT_FEED_COLUMN_IDS.LAST_UPDATED_BY,
      type: "CUSTOM",
      label: t("common:last_updated_by"),
      uiType: FEED_LIST_UI_TYPES.TEXT_WITH_AVATAR_AND_SUBTEXT,
      hasSortingEnabled: true,
      sortType: "toggle",
      entity: "TRANSCRIPT_LAST_UPDATED_AT",
      menuItemEntity: "TRANSCRIPT_LAST_UPDATED_AT",
      style: {
        minWidth: "180px",
        maxWidth: "180px",
      },
    },
    {
      id: STUDENT_FEED_COLUMN_IDS.LAST_GENERATED,
      type: "CUSTOM",
      label: t("common:last_generated"),
      uiType: FEED_LIST_UI_TYPES.TEXT,
      hasSortingEnabled: true,
      sortType: "toggle",
      entity: "TRANSCRIPT_LAST_GENERATED_AT",
      menuItemEntity: "TRANSCRIPT_LAST_GENERATED_AT",
      style: {
        minWidth: "180px",
        maxWidth: "180px",
      },
    },
    {
      id: STUDENT_FEED_COLUMN_IDS.DOWNLOAD,
      type: "CUSTOM",
      label: "",
      uiType: FEED_LIST_UI_TYPES.DOWNLOAD_BUTTON,
      style: {
        width: "48px",
      },
    }
  );

  return columns;
};

export const transformStudentsToFeedData = students => {
  return _.map(students, student => {
    const id = _.get(student, "id");
    const profileImage = _.get(student, "profileImage");
    const sourceId = _.get(student, "sourceId", "");

    const curriculumPrograms = _.get(student, "curriculumPrograms", []);
    const schoolName = _.get(_.first(curriculumPrograms), "label", "");

    const yearGroupName = _.get(student, "yearGroup.name", "");
    const globalGradeName = _.get(student, "yearGroup.grade.name", "");
    const transcriptSummary = _.get(student, "transcriptSummary");

    const gradeLabels = _.flatMap(
      _.get(transcriptSummary, "grades", []),
      grade => _.get(grade, "grade.name", [])
    );
    const lastUpdatedBy = _.get(transcriptSummary, "lastUpdatedBy", null);
    const lastUpdatedByName = lastUpdatedBy
      ? getFormattedUserName({
          user: lastUpdatedBy,
          type: "staff",
        })
      : null;

    const lastUpdatedAt = _.get(transcriptSummary, "lastUpdatedAt", null);

    const lastUpdatedAtFormatted = lastUpdatedAt
      ? moment(lastUpdatedAt).format("DD MMM YYYY, hh:mm")
      : "-";

    const lastGeneratedAt = _.get(
      transcriptSummary,
      "lastTranscriptGeneratedAt",
      null
    );

    const lastGeneratedAtFormatted = lastGeneratedAt
      ? moment(lastGeneratedAt).format("DD MMM YYYY, hh:mm a")
      : "-";

    const isArchived = _.get(student, "isArchivedV2", false);

    const label = getFormattedUserName({ user: student, type: "student" });

    return {
      id,
      isArchived,
      [STUDENT_FEED_COLUMN_IDS.STUDENT_NAME]: {
        label,
        subText: sourceId,
        showAvatar: true,
        src: profileImage,
      },
      [STUDENT_FEED_COLUMN_IDS.SCHOOL]: {
        label: schoolName,
      },
      [STUDENT_FEED_COLUMN_IDS.YEAR_GROUP]: {
        label: yearGroupName,
        subText: globalGradeName,
      },
      [STUDENT_FEED_COLUMN_IDS.GRADE_LEVELS]: gradeLabels,
      [STUDENT_FEED_COLUMN_IDS.LAST_UPDATED_BY]: {
        label: lastUpdatedByName,
        subText: lastUpdatedAtFormatted,
        showAvatar: false,
        showDash: !lastUpdatedByName,
      },
      [STUDENT_FEED_COLUMN_IDS.LAST_GENERATED]: lastGeneratedAtFormatted,
      [STUDENT_FEED_COLUMN_IDS.DOWNLOAD]: {
        id,
        schoolId: _.get(_.first(curriculumPrograms), "id", null),
        studentName: label,
      },
    };
  });
};

export const getSelectedRouteFromPath = path => {
  const segments = _.split(path, "/").filter(Boolean);
  const transcriptBaseRouteIndex = _.indexOf(
    segments,
    TRANSCRIPT_ROUTES.BASE_ROUTE
  );

  if (
    transcriptBaseRouteIndex !== -1 &&
    segments.length > transcriptBaseRouteIndex + 1
  ) {
    const routesToPathMap = _.invert(SIDEBAR_NODE_ID_TO_ROUTE_PATH_MAP);
    const selectedRoute = segments[transcriptBaseRouteIndex + 1];

    const currentRoute = routesToPathMap[selectedRoute];
    return currentRoute || null;
  }

  return null;
};

export const getFilterLabelValueFromOptionsAndSelectedValues = ({
  options,
  selectedValues,
  t,
}) => {
  if (_.size(selectedValues) === 0) return t("common:none");
  if (_.size(selectedValues) === _.size(options)) return t("common:all");
  return selectedValues;
};

export const getTemplateLayoutTypeAndOrientationFromBody = body => {
  if (!body)
    return {
      layoutType: "GRID",
      pageOrientation: "PORTRAIT",
    };

  return {
    layoutType: _.get(body, "document.page.layout", "GRID"),
    pageOrientation: _.get(body, "document.page.orientation", "PORTRAIT"),
  };
};

export const getUniqueName = ({
  value,
  entityId,
  entities,
  valueKey = "name",
}) => {
  const trimmedValue = _.trim(value);
  if (!trimmedValue) return trimmedValue;

  // Convert various collection types to array
  let entitiesArray = [];
  if (_.isArray(entities)) {
    entitiesArray = entities;
  } else if (entities instanceof Map) {
    entitiesArray = Array.from(entities.values());
  } else if (_.isObject(entities)) {
    entitiesArray = _.values(entities);
  }

  // Build set of existing values (case-insensitive, excluding current entity)
  const existingValues = new Set(
    _.chain(entitiesArray)
      .filter(entity => entity.id !== entityId)
      .map(entity => _.toLower(_.trim(_.get(entity, valueKey, ""))))
      .value()
  );

  // If no duplicate, return original value
  if (!existingValues.has(_.toLower(trimmedValue))) {
    return trimmedValue;
  }

  // Generate unique name with suffix: "Name 1", "Name 2", etc.
  let suffix = 1;
  let newValue = `${trimmedValue} ${suffix}`;
  while (existingValues.has(_.toLower(newValue))) {
    suffix++;
    newValue = `${trimmedValue} ${suffix}`;
  }

  return newValue;
};

export const handleRouterBack = (router, nextLocation) => {
  const nextPathname = _.get(nextLocation, "pathname", "");
  const nextSearch = _.get(nextLocation, "search", "");
  const currentSearch = _.get(router, "location.search", "");
  if (nextSearch !== currentSearch) {
    router.replace({
      pathname: nextPathname,
      search: currentSearch,
    });
    return false;
  }

  return true;
};

export const reorderArrayItems = (items, startIdx, endIdx) => {
  if (
    !items ||
    startIdx < 0 ||
    endIdx < 0 ||
    startIdx >= items.length ||
    endIdx >= items.length
  ) {
    return items;
  }
  const newItems = [...items];
  const [moved] = newItems.splice(startIdx, 1);
  newItems.splice(endIdx, 0, moved);
  return newItems;
};

export const resolveFlexHeaders = ({ headers, containerWidth = 766 }) => {
  const parentWidth = containerWidth - scrollbarWidth - 2;
  const fixedHeaders = _.filter(headers, h => !h.flex);
  const flexHeaders = _.filter(headers, h => h.flex);

  const totalDefinedWidth = _.sumBy(fixedHeaders, "width") || 0;
  const remainingWidth = parentWidth - totalDefinedWidth;

  if (flexHeaders.length > 0 && remainingWidth > 0) {
    const additionalWidthPerColumn = remainingWidth / flexHeaders.length;
    return _.map(headers, h =>
      h.flex ? { ...h, width: additionalWidthPerColumn } : h
    );
  }
  return headers;
};
